import { usePermissions, useUserPermissions } from '@cheqroom/hooks';
import type { TFunction } from 'i18next';
import { FC, ReactElement } from 'react';
import { Trans } from 'react-i18next';

import { Icon } from '../../../Icon';
import { Info } from '../../../Info';
import { useInfoT } from '../../../Info/Info.context';
import { useItemInfoContext } from '../../ItemInfo.context';
import { getCheckoutQuantities, getStatusColor, getStatusIcon, getStatusText, isItemUnavailable } from './utils';

type Props = {
	renderCustodyLink: (userId: string) => ReactElement;
};

export const StatusContent: FC<Props> = ({ renderCustodyLink }) => {
	const { item, user, workspace, featureFlags } = useItemInfoContext();
	const t = useInfoT('item-info');
	const permissions = usePermissions({ user: user ?? null, workspace: workspace ?? null });
	const userPermissions = useUserPermissions({ user: user ?? null, workspace: workspace ?? null });
	const canUseOrders = !!permissions.useCheckouts;
	const canUseReservations = !!permissions.useReservations;
	const canSeeUsers = userPermissions.canSeeUsers();

	// item.kit is only filled in when viewing a bulk item from its kit's own detail page — the
	// general items list never sets it, so this stays 0 there and we fall through to the loose branch.
	const { kitQuantity, checkedOutKitQuantity, totalCheckedOutQuantity } = getCheckoutQuantities(item);
	const noQuantityLeft =
		item.kind === 'bulk' &&
		(kitQuantity ? kitQuantity === checkedOutKitQuantity : item.quantity === totalCheckedOutQuantity);

	const isUnavailable = isItemUnavailable(item, canUseOrders, canUseReservations);
	const StatusIcon = getStatusIcon(item);
	const { key, values } = getStatusText(item, { isUnavailable, canSeeUsers });
	const i18nKey = `status.${key === 'expired' ? 'retired' : key}`;

	const text =
		key === 'assigned_with_link' ? (
			<Trans
				// `Trans`'s `t` prop requires the real, branded i18next `TFunction`, not just something
				// call-compatible — `Translate` deliberately isn't that (see Info.context.tsx). This
				// cast is internal-only: both sides are this package's own i18next copy, no cross-workspace
				// type-identity issue here, just satisfying `Trans`'s stricter prop type.
				t={t as unknown as TFunction}
				i18nKey={i18nKey}
				values={values}
				// react-i18next's `components` accepts `readonly ReactElement[] | Record<string, ReactElement>`
				// — a real union its own generic signature narrows further per call, in a way a
				// non-literal array (built from calling renderCustodyLink) doesn't satisfy even after
				// matching the element type exactly. Same class of friction as the cross-workspace
				// React type-identity casts on Avatar/Icon; `as any` is the narrow, local escape hatch.
				// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
				components={[renderCustodyLink(item.custody?._id ?? '')] as any}
			/>
		) : (
			t(i18nKey, values)
		);

	return (
		<Info
			icon={
				<Icon
					source={StatusIcon}
					size="small"
					className={getStatusColor(item, isUnavailable || noQuantityLeft)}
				/>
			}
			text={featureFlags?.hide_item_status_text ? undefined : text}
		/>
	);
};
